import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { variableSchema, VariableFormData } from '../schemas/model.schemas';
import { Variable, EnumOption, PricingTier, LookupTable } from '../types/model.types';
import { VariableType } from '@/types';
import { useModelStore } from '../store/model-store';
import { useToast } from '@/lib/hooks/use-toast';
import { validateFormula } from '../utils/formula-parser';
import { Loader2, CheckCircle, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { FormulaEditor } from './formula-editor';
import { TierEditor } from './tier-editor';

interface VariableFormProps {
  modelId: string;
  variable?: Variable;
  availableVariables: string[];
  lookupTables?: LookupTable[];
  onSuccess: () => void;
  onCancel: () => void;
}

export function VariableForm({
  modelId,
  variable,
  availableVariables,
  lookupTables = [],
  onSuccess,
  onCancel,
}: VariableFormProps) {
  const { createVariable, updateVariable, isLoading } = useModelStore();
  const { toast } = useToast();
  const [formulaValidation, setFormulaValidation] = useState<{
    isValid: boolean;
    error?: string;
    warnings?: string[];
  } | null>(null);

  // Local state for ENUM options and TIERED tiers
  const [enumOptions, setEnumOptions] = useState<EnumOption[]>(
    variable?.options || [
      { label: '', value: 0 },
      { label: '', value: 0 },
    ]
  );
  const [tiers, setTiers] = useState<PricingTier[]>(variable?.tiers || []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    control,
    reset,
    setValue,
  } = useForm<VariableFormData>({
    resolver: zodResolver(variableSchema),
    defaultValues: variable
      ? {
          name: variable.name,
          code: variable.code,
          variableType: variable.variableType,
          defaultValue: variable.defaultValue,
          formula: variable.formula || '',
          description: variable.description || '',
          isGlobal: variable.isGlobal || false,
          priceRole: variable.priceRole || null,
          order: variable.order,
          options: variable.options || undefined,
          tiers: variable.tiers || undefined,
          lookupTableId: variable.lookupTableId || undefined,
        }
      : {
          name: '',
          code: '',
          variableType: VariableType.NUMBER,
          defaultValue: undefined,
          formula: '',
          description: '',
          isGlobal: false,
          priceRole: null,
          order: availableVariables.length,
          options: undefined,
          tiers: undefined,
          lookupTableId: undefined,
        },
  });

  // Reset form when variable data changes (important for edit mode)
  useEffect(() => {
    if (variable) {
      reset({
        name: variable.name,
        code: variable.code,
        variableType: variable.variableType,
        defaultValue: variable.defaultValue,
        formula: variable.formula || '',
        description: variable.description || '',
        isGlobal: variable.isGlobal || false,
        priceRole: variable.priceRole || null,
        order: variable.order,
        options: variable.options || undefined,
        tiers: variable.tiers || undefined,
        lookupTableId: variable.lookupTableId || undefined,
      });
      setEnumOptions(
        variable.options || [
          { label: '', value: 0 },
          { label: '', value: 0 },
        ]
      );
      setTiers(variable.tiers || []);
    }
  }, [variable, reset]);

  const selectedType = watch('variableType');
  const formula = watch('formula');

  // Sync enum options to form
  useEffect(() => {
    if (selectedType === VariableType.ENUM) {
      setValue('options', enumOptions);
    }
  }, [enumOptions, selectedType, setValue]);

  // Sync tiers to form
  useEffect(() => {
    if (selectedType === VariableType.TIERED) {
      setValue('tiers', tiers);
    }
  }, [tiers, selectedType, setValue]);

  // Validate formula in real-time
  useEffect(() => {
    if (selectedType === VariableType.CALCULATED && formula) {
      const result = validateFormula(
        formula,
        availableVariables.filter((v) => v !== variable?.code)
      );
      setFormulaValidation(result);
    } else {
      setFormulaValidation(null);
    }
  }, [formula, selectedType, availableVariables, variable?.code]);

  const onSubmit = async (data: VariableFormData) => {
    // Validación adicional para fórmulas
    if (data.variableType === VariableType.CALCULATED && data.formula) {
      const validation = validateFormula(
        data.formula,
        availableVariables.filter((v) => v !== variable?.code)
      );

      if (!validation.isValid) {
        toast({
          title: 'Fórmula inválida',
          description: validation.error,
          variant: 'destructive',
        });
        return;
      }
    }

    try {
      const submitData = { ...data };

      // Attach enum options
      if (data.variableType === VariableType.ENUM) {
        submitData.options = enumOptions.filter((o) => o.label.trim());
      } else {
        submitData.options = null;
      }

      // Attach tiers
      if (data.variableType === VariableType.TIERED) {
        submitData.tiers = tiers;
      } else {
        submitData.tiers = null;
      }

      // Clear formula if not CALCULATED
      if (data.variableType !== VariableType.CALCULATED) {
        submitData.formula = undefined;
      }

      // Clear lookupTableId if not LOOKUP
      if (data.variableType !== VariableType.LOOKUP) {
        submitData.lookupTableId = null;
      }

      if (variable) {
        await updateVariable(modelId, variable.id, submitData as any);
        toast({ title: 'Variable actualizada', variant: 'success' });
      } else {
        await createVariable(modelId, submitData as any);
        toast({ title: 'Variable creada', variant: 'success' });
      }
      onSuccess();
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  // ENUM option handlers
  const handleAddOption = () => {
    setEnumOptions([...enumOptions, { label: '', value: 0 }]);
  };

  const handleRemoveOption = (index: number) => {
    setEnumOptions(enumOptions.filter((_, i) => i !== index));
  };

  const handleOptionChange = (index: number, field: 'label' | 'value', val: string) => {
    setEnumOptions(
      enumOptions.map((opt, i) =>
        i === index ? { ...opt, [field]: field === 'value' ? parseFloat(val) || 0 : val } : opt
      )
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Nombre *</Label>
          <Input
            id="name"
            placeholder="Costo de Licencias"
            {...register('name')}
            error={!!errors.name}
            disabled={isLoading}
          />
          {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="code">Código *</Label>
          <Input
            id="code"
            placeholder="COSTO_LICENCIAS"
            {...register('code')}
            error={!!errors.code}
            disabled={isLoading || !!variable}
            className="uppercase font-mono"
          />
          {errors.code && <p className="text-sm text-destructive">{errors.code.message}</p>}
          <p className="text-xs text-muted-foreground">
            Mayúsculas, números y guión bajo (_). Ej: COSTO_BASE
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="variableType">Tipo *</Label>
        <Controller
          name="variableType"
          control={control}
          defaultValue={variable?.variableType || VariableType.NUMBER}
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange} disabled={isLoading}>
              <SelectTrigger id="variableType">
                <SelectValue placeholder="Selecciona un tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={VariableType.NUMBER}>Número</SelectItem>
                <SelectItem value={VariableType.PERCENTAGE}>Porcentaje</SelectItem>
                <SelectItem value={VariableType.CURRENCY}>Moneda</SelectItem>
                <SelectItem value={VariableType.TEXT}>Texto</SelectItem>
                <SelectItem value={VariableType.BOOLEAN}>Booleano</SelectItem>
                <SelectItem value={VariableType.CALCULATED}>Calculada (con formula)</SelectItem>
                <SelectItem value={VariableType.ENUM}>Enum (dropdown)</SelectItem>
                <SelectItem value={VariableType.TIERED}>Escalonada (por volumen)</SelectItem>
                <SelectItem value={VariableType.LOOKUP}>Lookup (matriz)</SelectItem>
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {/* CALCULATED: Formula editor */}
      {selectedType === VariableType.CALCULATED && (
        <div className="space-y-2">
          <Label htmlFor="formula">Formula *</Label>
          <Controller
            name="formula"
            control={control}
            render={({ field }) => (
              <FormulaEditor
                value={field.value || ''}
                onChange={field.onChange}
                availableVariables={availableVariables.filter((v) => v !== variable?.code)}
                placeholder="COSTO_BASE * CANTIDAD + 100"
                disabled={isLoading}
              />
            )}
          />
          {errors.formula && <p className="text-sm text-destructive">{errors.formula.message}</p>}

          {formulaValidation && (
            <div className="space-y-2">
              <div
                className={`p-3 rounded-lg flex items-start gap-2 ${
                  formulaValidation.isValid
                    ? 'bg-ok-suave text-ok'
                    : 'bg-bloqueo-suave text-bloqueo'
                }`}
              >
                {formulaValidation.isValid ? (
                  <>
                    <CheckCircle className="w-4 h-4 mt-0.5" />
                    <span className="text-sm">Formula valida</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-4 h-4 mt-0.5" />
                    <span className="text-sm">{formulaValidation.error}</span>
                  </>
                )}
              </div>

              {formulaValidation.warnings && formulaValidation.warnings.length > 0 && (
                <div className="p-3 rounded-lg bg-alerta-suave text-alerta border border-alerta-borde">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div className="space-y-1">
                      <span className="text-sm font-medium">Advertencias:</span>
                      <ul className="text-sm list-disc list-inside space-y-0.5">
                        {formulaValidation.warnings.map((warning, idx) => (
                          <li key={idx}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Variables disponibles:{' '}
            {availableVariables.filter((v) => v !== variable?.code).join(', ') || 'Ninguna'}
          </p>
        </div>
      )}

      {/* ENUM: Options builder */}
      {selectedType === VariableType.ENUM && (
        <div className="space-y-3">
          <Label>Opciones del Dropdown *</Label>
          <div className="space-y-2">
            {enumOptions.map((option, index) => (
              <div key={index} className="flex gap-2 items-center">
                <Input
                  placeholder="Etiqueta (ej: Express)"
                  value={option.label}
                  onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                  className="flex-1"
                  disabled={isLoading}
                />
                <Input
                  type="number"
                  placeholder="Valor"
                  step="0.01"
                  value={option.value}
                  onChange={(e) => handleOptionChange(index, 'value', e.target.value)}
                  className="w-32"
                  disabled={isLoading}
                />
                {enumOptions.length > 2 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    onClick={() => handleRemoveOption(index)}
                    disabled={isLoading}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                )}
              </div>
            ))}
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddOption}
            disabled={isLoading}
          >
            <Plus className="w-4 h-4 mr-1" />
            Agregar Opción
          </Button>
          <p className="text-xs text-muted-foreground">
            El usuario selecciona una etiqueta del dropdown y la formula recibe el valor numerico.
          </p>
        </div>
      )}

      {/* TIERED: Tier editor */}
      {selectedType === VariableType.TIERED && (
        <TierEditor tiers={tiers} onChange={setTiers} disabled={isLoading} />
      )}

      {/* LOOKUP: Table selector */}
      {selectedType === VariableType.LOOKUP && (
        <div className="space-y-2">
          <Label>Tabla de Lookup</Label>
          <Controller
            name="lookupTableId"
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || undefined}
                onValueChange={field.onChange}
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tabla de lookup" />
                </SelectTrigger>
                <SelectContent>
                  {lookupTables.map((lt) => (
                    <SelectItem key={lt.id} value={lt.id}>
                      {lt.name} ({lt.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          {lookupTables.length === 0 && (
            <p className="text-xs text-muted-foreground">
              No hay tablas de lookup. Crea una primero en la sección de Tablas de Lookup del
              modelo.
            </p>
          )}
        </div>
      )}

      {/* Default value for simple types */}
      {selectedType !== VariableType.CALCULATED &&
        selectedType !== VariableType.ENUM &&
        selectedType !== VariableType.TIERED &&
        selectedType !== VariableType.LOOKUP && (
          <div className="space-y-2">
            <Label htmlFor="defaultValue">Valor por Defecto</Label>
            <Input
              id="defaultValue"
              type={
                selectedType === VariableType.NUMBER ||
                selectedType === VariableType.PERCENTAGE ||
                selectedType === VariableType.CURRENCY
                  ? 'number'
                  : 'text'
              }
              step="any"
              placeholder="0"
              {...register('defaultValue')}
              error={!!errors.defaultValue}
              disabled={isLoading}
            />
            {errors.defaultValue && (
              <p className="text-sm text-destructive">{errors.defaultValue.message as string}</p>
            )}
          </div>
        )}

      <div className="space-y-2">
        <Label htmlFor="description">Descripción</Label>
        <Textarea
          id="description"
          placeholder="Describe esta variable..."
          rows={2}
          {...register('description')}
          disabled={isLoading}
        />
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="isGlobal"
          {...register('isGlobal')}
          disabled={isLoading}
          className="w-4 h-4"
        />
        <Label htmlFor="isGlobal" className="cursor-pointer">
          Variable Global
        </Label>
        <p className="text-xs text-muted-foreground ml-2">
          Las variables globales se comparten entre todos los items de una cotizacion (ej: tipo de
          cambio, IVA)
        </p>
      </div>

      <div className="space-y-2 p-4 rounded-lg border-2 border-dashed border-primary/30 bg-primary/5">
        <Label htmlFor="priceRole" className="text-sm font-semibold">
          Rol en Cotización
        </Label>
        <Controller
          name="priceRole"
          control={control}
          render={({ field }) => (
            <Select
              value={field.value || 'NONE'}
              onValueChange={(v) => field.onChange(v === 'NONE' ? null : v)}
              disabled={isLoading}
            >
              <SelectTrigger id="priceRole">
                <SelectValue placeholder="Ninguno" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">Ninguno — variable normal</SelectItem>
                <SelectItem value="COST_INPUT">
                  Costo de Entrada — costo crudo del proveedor (ej: VALOR_FOB)
                </SelectItem>
                <SelectItem value="COST_BASE_MARGIN">
                  Costo Base Margen — base para el cálculo del margen (ej: costo + flete + GA)
                </SelectItem>
                <SelectItem value="UNIT_COST">
                  Costo Unitario — costo total por unidad (opcional)
                </SelectItem>
                <SelectItem value="QUANTITY">Cantidad</SelectItem>
                <SelectItem value="UNIT_PRICE">
                  Precio Unitario — se muestra como precio por unidad
                </SelectItem>
                <SelectItem value="TOTAL_PRICE">
                  Total Línea — se muestra como total del item
                </SelectItem>
                <SelectItem value="MARGIN_PCT">
                  Margen % — porcentaje de margen aplicado (ej: MARGEN_PCT)
                </SelectItem>
                <SelectItem value="MARGIN">
                  Margen (monto) — monto de ganancia en plata (ej: MONTO_MARGEN)
                </SelectItem>
                <SelectItem value="TAX">
                  Impuesto — monto pasante (impuestos/provisiones)
                </SelectItem>
              </SelectContent>
            </Select>
          )}
        />
        <p className="text-xs text-muted-foreground">
          Marca qué rol cumple esta variable en la cotización. Solo puede haber una variable por rol
          en cada modelo.
        </p>
        <details className="text-xs bg-muted/40 rounded-md p-2.5 border border-muted">
          <summary className="cursor-pointer font-medium">¿Cuándo usar cada rol?</summary>
          <ul className="mt-2 space-y-1.5 text-muted-foreground leading-relaxed list-disc list-inside">
            <li>
              <strong className="text-primary">Costo de Entrada</strong>: variable donde entra el
              costo crudo del proveedor (ej. <code>VALOR_FOB</code>).
            </li>
            <li>
              <strong className="text-info">Costo Base Margen</strong>: base sobre la que se aplica
              el <strong>% de margen</strong>. En importaciones = costo + flete + GA + impuestos.
            </li>
            <li>
              <strong className="text-primary">Costo Unitario</strong>: alias de Costo de Entrada
              (legacy).
            </li>
            <li>
              <strong className="text-violet-700">Cantidad</strong>: la variable CANTIDAD.
            </li>
            <li>
              <strong className="text-alerta">Precio Unitario / Total Línea</strong>: precios
              finales al cliente.
            </li>
            <li>
              <strong className="text-ok">Margen %</strong>: el porcentaje de margen que aplica el
              vendedor (ej. <code>MARGEN_PCT = 25</code>). Es un input/setting del modelo.
            </li>
            <li>
              <strong className="text-ok">Margen (monto)</strong>: monto de ganancia en plata (NO el
              %). Ej. <code>MONTO_MARGEN = COSTO_BASE × MARGEN_PCT / 100</code>. Es el resultado del
              cálculo.
            </li>
            <li>
              <strong className="text-alerta">Impuesto (monto)</strong>: monto pasante (IVA,
              provisiones, retenciones).
            </li>
          </ul>
          <p className="mt-2 text-[11px] italic">
            Margen Real que se muestra en la cotización = Margen (monto) / Costo Base Margen × 100.
          </p>
        </details>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="order">Orden (posición en cascada) *</Label>
          <Input
            id="order"
            type="number"
            min="0"
            step="1"
            placeholder="0"
            {...register('order', { valueAsNumber: true })}
            error={!!errors.order}
            disabled={isLoading}
          />
          {errors.order && <p className="text-sm text-destructive">{errors.order.message}</p>}
          <p className="text-xs text-muted-foreground">
            Define la posición en la tabla de cotización. Las formulas deben tener un orden mayor
            que las variables que referencian.
          </p>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button
          type="submit"
          disabled={
            isLoading ||
            (selectedType === VariableType.CALCULATED &&
              !!formulaValidation &&
              !formulaValidation.isValid)
          }
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : variable ? (
            'Actualizar'
          ) : (
            'Crear Variable'
          )}
        </Button>
      </div>
    </form>
  );
}

export default VariableForm;
